// src/components/Admins/RoleManagement.jsx
import React, { useEffect, useState } from "react";
import "./RoleManagement.css";
import API from "../services/api";
import { loadAdminUsers, saveAdminUsers } from "../utils/adminUsersStorage";

const defaultPermissions = {
  SUPER_ADMIN: ["ALL_ACCESS"],
  HR_ADMIN: ["VIEW_JOBS", "MANAGE_APPLICATIONS"],
  CONTENT_MANAGER: ["CREATE_BLOG", "EDIT_BLOG"],
};

const allPermissions = [
  "ALL_ACCESS",
  "VIEW_JOBS",
  "MANAGE_APPLICATIONS",
  "CREATE_BLOG",
  "EDIT_BLOG",
];

const RoleManagement = () => {
  const [admins, setAdmins] = useState(() => loadAdminUsers());

  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [permissions, setPermissions] = useState([]);

  const selectedAdmin = admins.find((admin) => admin.id === selectedAdminId);

  useEffect(() => {
    saveAdminUsers(admins);
  }, [admins]);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const res = await API.get("/admin/all/admins");

        const apiAdmins = (res.data.data || res.data || []).map((admin) => {
          const storedAdmin = loadAdminUsers().find(
            (storedItem) => storedItem.id === admin.id
          );

          return {
            id: admin.id,
            name: admin.username || storedAdmin?.name || "",
            email: admin.email || storedAdmin?.email || "",
            role: admin.role || storedAdmin?.role || "",
            password: storedAdmin?.password || "",
            permissions:
              storedAdmin?.permissions?.length
                ? storedAdmin.permissions
                : defaultPermissions[admin.role] || [],
          };
        });

        setAdmins(apiAdmins);
      } catch (error) {
        console.log(error);
      }
    };

    fetchAdmins();
  }, []);

  useEffect(() => {
    if (!selectedAdminId) return;

    const currentAdmin = admins.find((admin) => admin.id === selectedAdminId);

    if (!currentAdmin) return;

    setPermissions(
      currentAdmin.permissions?.length
        ? currentAdmin.permissions
        : defaultPermissions[currentAdmin.role] || []
    );
  }, [admins, selectedAdminId]);

  const handleSelectAdmin = (admin) => {
    setSelectedAdminId(admin.id);

    setPermissions(
      admin.permissions?.length
        ? admin.permissions
        : defaultPermissions[admin.role] || []
    );
  };

  const handlePermissionToggle = (permission) => {
    setPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission]
    );
  };

  const handleSave = () => {
    if (!selectedAdminId) {
      alert("Please select an admin user first");
      return;
    }

    setAdmins((prevAdmins) =>
      prevAdmins.map((admin) =>
        admin.id === selectedAdminId
          ? { ...admin, permissions: [...permissions] }
          : admin
      )
    );

    alert("Permissions Updated Successfully");
  };

  return (
    <div className="role-container">
      <section className="role-title-wrap">
        <div>
          <h2 className="role-title">Role Management</h2>
          <p className="role-subtitle">
            Select an admin and configure permissions.
          </p>
        </div>

        <div className="role-meta">
          <span className="role-pill">Total Admins: {admins.length}</span>
          <span className="role-pill">
            Active Selection: {selectedAdmin?.name || "None"}
          </span>
        </div>
      </section>

      <div className="role-grid">

        {/* ADMIN LIST */}

        <div className="role-card">
          <div className="role-card-head">
            <h3>Admin Users</h3>
            <small>Choose one to configure</small>
          </div>

          <ul className="admin-list">
            {admins.map((admin) => (
              <li
                key={admin.id}
                onClick={() => handleSelectAdmin(admin)}
                className={
                  selectedAdminId === admin.id ? "active-admin" : ""
                }
              >
                <div className="admin-name-wrap">
                  <span className="admin-name">{admin.name}</span>
                  <span className="admin-id">ID #{admin.id}</span>
                </div>

                {/* ROLE HIGHLIGHT */}
                <span className="role-badge">{admin.role || "NO_ROLE"}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* PERMISSIONS */}

        <div className="role-card">

          <h4 className="permission-title">
            Permissions ({selectedAdmin?.role || "No Role"})
          </h4>

          <div className="permission-list">
            {allPermissions.map((permission) => (
              <label
                key={permission}
                className={`permission-item ${
                  permissions.includes(permission) ? "permission-active" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={permissions.includes(permission)}
                  onChange={() => handlePermissionToggle(permission)}
                />

                {permission}
              </label>
            ))}
          </div>

          <div className="role-action-buttons">
            <button
              className="save-role-btn"
              onClick={handleSave}
              disabled={!selectedAdminId}
            >
              Update Permissions
            </button>
          </div>

        </div>

      </div>
    </div>
  );
};

export default RoleManagement;
